import type { LongFormAiEvent } from "@expert/sales-tools";

import {
    buildHarnessNarrativeEvent,
    buildHarnessVariantNarrativeEvent,
    LONG_FORM_AI_EVENT,
    useLongFormAiStore,
} from "@expert/sales-tools";
import { expertWorkspaceWebSocketEventBus } from "@expert/websocket";
import { Button, Divider, Group, Stack, Text, TextInput } from "@mantine/core";
import { useCallback, useEffect, useState } from "react";

type Props = {
    sessionId: string;
};

const EMIT_SUCCESS_DURATION_MS = 2000;
const CHANNEL = `ws_${LONG_FORM_AI_EVENT}` as const;
// The client reads sessionId/sku/narrative off the payload; employeeId rides along for the producer
// contract only, so any value works here.
const DEV_EMPLOYEE_ID = "dev-tools";

/**
 * Forces every Long Form AI surface state by hand. Emits on the REAL websocket bus rather than writing
 * to the store, so a press exercises the whole path the producer will — listener validation, the
 * contactId→sessionId resolve, and the guided-browsing lookup — not just the store reducer.
 */
export function LongFormAiTab({ sessionId }: Props) {
    const [emitted, setEmitted] = useState("");
    // Emitting under a different id models a callback/conference contact, where the event's sessionId is
    // the Amazon Connect contactId and the wrapper has to resolve it to this session.
    const [sessionIdOverride, setSessionIdOverride] = useState("");
    const target = sessionIdOverride || sessionId;

    useEffect(() => {
        if (!emitted) return;
        const id = setTimeout(() => setEmitted(""), EMIT_SUCCESS_DURATION_MS);
        return () => clearTimeout(id);
    }, [emitted]);

    // Typed to the real contract so the well-formed buttons are actually checked. The bus itself types
    // every WS payload as `any`, so it would accept anything — the type lives here instead, and the one
    // deliberately off-contract case (Malformed) casts at its own call site.
    const emit = useCallback((label: string, payload: LongFormAiEvent) => {
        expertWorkspaceWebSocketEventBus.emit(CHANNEL, payload);
        setEmitted(label);
    }, []);

    return (
        <Stack gap="sm">
            <Text size="xs" c="dimmed">
                Emits on the real WS bus. Narrative A then B gives you the “New script available” banner.
            </Text>

            <Divider label="Narratives" labelPosition="left" />

            <Group gap="xs">
                <Button
                    size="xs"
                    variant="light"
                    color="violet"
                    onClick={() => emit("A", buildHarnessNarrativeEvent(target, DEV_EMPLOYEE_ID))}
                >
                    Narrative A
                </Button>
                <Button
                    size="xs"
                    variant="light"
                    color="violet"
                    onClick={() => emit("B (variant)", buildHarnessVariantNarrativeEvent(target, DEV_EMPLOYEE_ID))}
                >
                    Narrative B (variant)
                </Button>
            </Group>
            <Text size="xs" c="dimmed">
                B drops offer-2, adds offer-4, and rewrites the rest — so the swap, the carried read state and the new
                pending beat are all visible.
            </Text>

            <Divider label="Edge cases" labelPosition="left" />

            <Group gap="xs">
                <Button
                    size="xs"
                    variant="light"
                    color="violet"
                    onClick={() =>
                        emit("other SKU", {
                            ...buildHarnessVariantNarrativeEvent(target, DEV_EMPLOYEE_ID),
                            sku: "9999",
                        })
                    }
                >
                    Different SKU
                </Button>
                <Button
                    size="xs"
                    variant="light"
                    color="orange"
                    onClick={() =>
                        // Deliberately off-contract (no narrative/employeeId): the discard path is the case
                        // under test, so this is the one call that opts out of the typing above.
                        emit("malformed", { sessionId: target, sku: "3002" } as unknown as LongFormAiEvent)
                    }
                >
                    Malformed
                </Button>
            </Group>
            <Text size="xs" c="dimmed">
                Different SKU is dropped with a metric once a sale is on screen — this surface only ever shows the
                selected product. Malformed should be discarded with a metric and leave the surface untouched.
            </Text>

            <Divider label="Session" labelPosition="left" />

            <TextInput
                placeholder={`contactId to emit under (default: ${sessionId || "no session"})`}
                value={sessionIdOverride}
                onChange={(e) => setSessionIdOverride(e.target.value)}
                size="xs"
            />

            <Group gap="xs" align="center">
                <Button
                    size="xs"
                    variant="light"
                    color="red.5"
                    onClick={() => {
                        useLongFormAiStore.getState().reset(target);
                        setEmitted("reset");
                    }}
                >
                    Reset
                </Button>
                {emitted && (
                    <Text size="xs" c="green.3" fw={500}>
                        Emitted {emitted}
                    </Text>
                )}
            </Group>
        </Stack>
    );
}
